import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Box, Paper, Typography, TextField, Stack, CircularProgress } from '@mui/material';
import { Button, Chip } from 'components/Form';
import { getCurrentSemesterAndYear } from 'utils/general';
import { semesters, statuses } from 'keys';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getStudentSummaryFetch, updateTeacherRecommendationFetch } from 'store/middlewares/assessment.crud';
import { useSelector } from 'react-redux';
import { PaginationArea, ScrollableArea, StudentProgressStyles } from './Style';
import CreateIcon from '@mui/icons-material/Create';
import CustomPagination from 'components/Table/FooterPagination';
import { initialPage, initialPageSize } from 'keys/pagination';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'; // או כל אייקון שתרצי
import { ratingListTheme } from 'keys/rating';
import { gTheme } from 'styles/theme';

const getRatingColor = (ratingIndex) => {
    if (!ratingIndex) return undefined
    const themeKey = ratingListTheme[ratingIndex - 1]
    return gTheme?.[themeKey]?.main
}

// אותה לוגיקה כמו StatusCell בתרגולים פעילים — status+sub_status → תצוגה
const getAssessmentStatusMeta = (status, subStatus, assessmentSemesterId) => {
    const {semester} = getCurrentSemesterAndYear();

    if (!status) return null
    if (status === 'CREATED') return { label: "לא בוצע", color: "gray" }
    if (status === 'UPLOADING' && subStatus == null) return { label: assessmentSemesterId===semester.id? "תקול": "הוחזר וחלף", color: "gray" }
    // if (status === 'UPLOADING' || status === 'FILE_UPLOADED') return { label: "ממתין לעיבוד", color: "gray" }
    if (status === 'FILE_UPLOADED') return { label: "בוצע", color: "gray" }
    if (status === 'REDO' || status === 'BROKEN_FILE') return { label: "ביצוע חוזר", color: "gray" }
    if (status === 'BACK_TO_MARKING') return { label: "בעיבוד", color: "gray" }
    if (status === 'IN_PROGRESS') {
        if (subStatus === 'MARKING_WORDS_COMPLETE') return null  // יש מספרים — לא מציגים chip
        if (subStatus === 'MARKING_WORDS_RECHECK_PENDING') return { label: "פענוח מורכב", color: "gray"}
        return { label: "בעיבוד", color: "gray" }
    }
    return null
}

// =========================================================================
// קומפוננטת כרטיס בודד (מאפשרת הדפסה פרטנית)
// =========================================================================
const StudentCard = ({
    student,
    checkTypes,
    numOfSemesters,
    semesters,
    recommendationsRef,
    updateRecommendation,
    isPending,
    variables,
    dynamicColumnsCount
}) => {
    // הרפרנס להדפסה פרטנית
    const printRef = useRef(null);
    const {brokenDisplay} = useSelector(state => state.general)

    // הגדרת הדפסה פרטנית
    const handlePrintIndividual = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `הערכה - ${student.student_name}`,
        pageStyle: `
            @page { size: A4 landscape; margin: 10mm; }
            html, body { 
                direction: rtl !important; 
                -webkit-print-color-adjust: exact !important; 
                print-color-adjust: exact !important;
                background-color: white !important;
            }
            .student-card {
                border: 1px solid #E8EEF6;
                border-radius: 8px;
            }
            .grid-container {
                display: grid !important;
                /* חובה: לשכפל לכאן את העמודות כדי שההדפסה תכיר אותן! */
                grid-template-columns: minmax(180px, 1.5fr) repeat(${dynamicColumnsCount}, minmax(60px, 1fr)) minmax(220px, 2fr) !important;
                width: 100% !important;
            }
            .grid-container > div {
                border-bottom: 1px solid #E8EEF6 !important;
                border-left: 1px solid #E8EEF6 !important;
                padding: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .grid-container .cell-start { justify-content: flex-start !important; }
            .grid-container .cell-between { justify-content: space-between !important; }
            
            button, .MuiButtonBase-root { display: none !important; }
            .recommendation-field fieldset { border: none !important; }
            .recommendation-field textarea { resize: none; overflow: hidden; }
        `
    });

    return (
        <Paper ref={printRef} elevation={0} className="student-card" sx={{ mb: 3 }}>
            <Box className="grid-container">
                {/* שם תלמיד */}
                <Box className={`cell-start ${brokenDisplay ? 'blurred2' : ''}`}>
                    <Chip color="gray">
                        {student.student_name}
                    </Chip>
                </Box>

                {/* כותרות ראשיות */}
                {checkTypes.map((t) => (
                    <Box key={`h-${t.id}`} className="cell-center header-cell" sx={{ gridColumn: `span ${numOfSemesters}` }}>
                        {t.label}
                    </Box>
                ))}

                <Box className="cell-between bold">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CreateIcon fontSize="small" sx={{ color: '#7E92A2' }} />
                        <Typography variant="body2" fontWeight="bold">המלצות המורה</Typography>
                    </Stack>
                </Box>

                {/* כותרת סמסטרים */}
                <Box className="cell-start subheader-cell">השוואה בין סמסטרים</Box>

                {/* תת כותרות סמסטרים */}
                {checkTypes.map((t) => (
                    <React.Fragment key={`sub-${t.id}`}>
                        {[...Array(numOfSemesters)].map((_, i) => (
                            <Box key={i} className="cell-center subheader-cell">
                                {semesters?.[i]?.label || `סמסטר ${i + 1}`}
                            </Box>
                        ))}
                    </React.Fragment>
                ))}

                {/* אזור המלצות */}
                <Box className="recommendation-box" sx={{ gridRow: `span ${student.metrics.length + 1}` }}>
                    <TextField
                        className="recommendation-field" fullWidth multiline minRows={4}
                        placeholder="כתבי המלצה להמשך עבודה עם התלמיד..."
                        defaultValue={student.teacher_recommendation}
                        onChange={(e) => recommendationsRef.current[student.id] = e.target.value}
                    />

                    {/* אזור הכפתורים: שמירה והדפסה פרטנית */}
                    <Stack direction="row" spacing={1} width="100%" justifyContent="flex-start">
                        <Button
                            color="dark" size="small" direction="rtl" variant="contained"
                            icon={<i className="fa-regular fa-floppy-disk"></i>}
                            disabled={isPending && variables?.id === student.recommendation_battery_id}
                            loading={isPending && variables?.id === student.recommendation_battery_id}
                            action={() => updateRecommendation({
                                id: student.recommendation_battery_id,
                                teacher_recommendation: recommendationsRef.current[student.id] ?? student.teacher_recommendation
                            })}
                            label="שמירה"
                        />

                        {/* כפתור הדפסה פרטנית */}
                        <Button
                            variant="outlined"
                            size="small"
                            color="dark" // או כל צבע מתאים שמוגדר לך
                            action={handlePrintIndividual}
                            icon={<LocalPrintshopIcon fontSize="small" />}
                            label="הדפסה"
                            // שימי לב שאנחנו מעלימים את הכפתור בזמן הדפסה כדי שלא ייראה בדף המודפס
                            sx={{
                                '@media print': { display: 'none' }
                            }}
                        />
                    </Stack>
                </Box>

                {/* שורות המדדים */}
                {student.metrics.map((metric) => (
                    <React.Fragment key={metric.id}>
                        <Box className="cell-start metric-name">{metric.category_name}</Box>
                        {checkTypes.map((t) => (
                            <React.Fragment key={`data-${metric.id}-${t.id}`}>
                                {[...Array(numOfSemesters)].map((_, i) => {
                                    const value = metric[t.id]?.[i]
                                    const hasValue = value !== null && value !== undefined && value !== ""
                                    const statusMeta = !hasValue
                                        ? getAssessmentStatusMeta(metric.status?.[i], metric.sub_status?.[i], i+1)
                                        : null
                                    const color = hasValue ? getRatingColor(metric[`${t.id}_rating`]?.[i]) : undefined
                                    return (
                                        <Box key={i} className="cell-center">
                                            {hasValue
                                                ? <span style={{ color }}>{`${value}${t.isPercent ? '%' : ''}`}</span>
                                                : statusMeta
                                                    ? <Chip color={statusMeta.color} size="small">{statusMeta.label}</Chip>
                                                    : "-"}
                                        </Box>
                                    )
                                })}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </Box>
        </Paper>
    );
};

export default StudentCard